import { memo, type ReactNode } from "react";
import {
  Mail,
  Phone,
} from "lucide-react";
import { Box, Button, Container, Typography } from "@mui/material";
import {
  siteContact,
  siteRegulatoryText,
} from "shared/config/site";
import oxyDtvmLogoWhite from "shared/assets/oxy-dtvm-logo-white.png";
import { openCookiePreferences } from "shared/feedback/cookiePreferences";
import type { NavigationActionItem } from "shared/navigation/types";

interface SiteFooterProps {
  homeAction?: () => void;
  institutionalLinks: NavigationActionItem[];
  governanceLinks: NavigationActionItem[];
}

export const SiteFooter = memo(function SiteFooter({
  homeAction,
  institutionalLinks,
  governanceLinks,
}: SiteFooterProps) {
  return (
    <Box component="footer" sx={{ bgcolor: "#0b3237", color: "var(--oxy-surface)" }}>
      <Container maxWidth="lg" sx={{ py: { xs: 8, lg: 10 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.05fr 0.85fr 0.85fr 0.95fr" },
            gap: 4,
            mb: 6,
          }}
        >
          <Box>
            <Box
              component="img"
              src={oxyDtvmLogoWhite}
              alt="OXY DTVM"
              sx={{
                display: "block",
                width: { xs: 180, lg: 220 },
                maxWidth: "100%",
                height: "auto",
                mb: 1.8,
              }}
            />
            <Typography sx={{ color: "var(--oxy-text-secondary)", lineHeight: 1.8, mb: 2.4 }}>
              Infraestrutura robusta, segura e regulada para o mercado de capitais,
              com atenção à governança, privacidade, canal de atendimento e
              transparência regulatória.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.4, mb: 2.4 }}>
              <Box sx={contactRowSx}>
                {siteContact.address}
              </Box>
              <Box component="a" href={siteContact.emailHref} sx={contactLinkSx}>
                <Mail size={16} />
                {siteContact.email}
              </Box>
              {siteContact.phones.map((phoneContact) => (
                <Box
                  key={phoneContact.value}
                  component="a"
                  href={phoneContact.href}
                  sx={contactLinkSx}
                >
                  <Phone size={16} />
                  {phoneContact.value}
                </Box>
              ))}
            </Box>
          </Box>

          <Box>
            <FooterTitle>Links institucionais</FooterTitle>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.1 }}>
              <Button onClick={homeAction} sx={footerLinkSx}>
                OXY DTVM
              </Button>
              {institutionalLinks.map((link) => (
                <Button
                  key={link.label}
                  onClick={link.onClick}
                  sx={footerLinkSx}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Box>
            <FooterTitle>Governança</FooterTitle>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.1 }}>
              {governanceLinks.map((link) => (
                <Button
                  key={link.label}
                  onClick={link.onClick}
                  sx={footerLinkSx}
                >
                  {link.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Box>
            <FooterTitle>DPO</FooterTitle>

            <Box
              sx={{
                p: 2.4,
                borderRadius: 3,
                border: "1px solid #1a5b63",
                bgcolor: "#0e3f45",
                mb: 2.4,
              }}
            >
              <Typography sx={{ color: "var(--oxy-surface-subtle)", mb: 0.8 }}>
                Encarregado de Proteção de Dados (DPO)
              </Typography>
              <Typography sx={{ color: "var(--oxy-text-secondary)", lineHeight: 1.75 }}>
                Encarregado de Proteção de Dados da OXY DTVM
              </Typography>
              <Box component="a" href={siteContact.dpoEmailHref} sx={footerInlineLinkSx}>
                {siteContact.dpoEmail}
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                {siteContact.phones.map((phoneContact) => (
                  <Box
                    key={phoneContact.value}
                    component="a"
                    href={phoneContact.href}
                    sx={footerInlineLinkSx}
                  >
                    {phoneContact.value}
                  </Box>
                ))}
              </Box>
              <Typography sx={{ color: "var(--oxy-text-secondary)", lineHeight: 1.75 }}>
                Segunda a sexta-feira, das 9h às 18h
              </Typography>
            </Box>

            <Button onClick={openCookiePreferences} sx={footerLinkSx}>
              Preferências de cookies
            </Button>
          </Box>
        </Box>

        <Box sx={{ mb: 6 }}>
          <Box
            sx={{
              p: { xs: 2.8, lg: 3.4 },
              borderRadius: 3,
              border: "1px solid #1a5b63",
              bgcolor: "#0e3f45",
            }}
          >
            <Box sx={{ maxWidth: 1120 }}>
              {siteRegulatoryText.map((paragraph, index) => (
                <Typography
                  key={paragraph}
                  sx={{
                    color: "var(--oxy-text-secondary)",
                    fontSize: { xs: "0.92rem", lg: "0.88rem" },
                    lineHeight: 1.72,
                    mb: index === siteRegulatoryText.length - 1 ? 0 : 1.6,
                  }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: "1px solid #1a5b63",
            pt: 3,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography sx={{ color: "#7f8b98", fontSize: "0.85rem" }}>
            © 2026 OXY DTVM. Todos os direitos reservados.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
});

function FooterTitle({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: "0.76rem",
        color: "var(--oxy-border-strong)",
        letterSpacing: "0.11em",
        textTransform: "uppercase",
        mb: 2.1,
      }}
    >
      {children}
    </Typography>
  );
}

const contactRowSx = {
  display: "inline-flex",
  alignItems: "flex-start",
  gap: 1,
  color: "var(--oxy-text-secondary)",
  fontSize: "0.92rem",
  lineHeight: 1.7,
};

const contactLinkSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 1,
  color: "var(--oxy-text-secondary)",
  textDecoration: "none",
  fontSize: "0.92rem",
  "&:hover": { color: "var(--oxy-surface)" },
};

const footerInlineLinkSx = {
  color: "var(--oxy-text-secondary)",
  textDecoration: "none",
  fontSize: "0.92rem",
  lineHeight: 1.75,
  "&:hover": { color: "var(--oxy-surface)" },
};

const footerLinkSx = {
  justifyContent: "flex-start",
  minWidth: 0,
  p: 0,
  color: "var(--oxy-text-secondary)",
  textTransform: "none",
  fontSize: "0.92rem",
  fontWeight: 400,
  "&:hover": {
    bgcolor: "transparent",
    color: "var(--oxy-surface)",
  },
};
